import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowRight, MoreHorizontal, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StrategicOptionsIntro } from "./StrategicOptionsIntro";
import { StrategicOptionCardCompact } from "./StrategicOptionCardCompact";
import { StrategicOption } from "./StrategicOptionCard";
import { AIReasoning } from "./AIReasoning";


interface Phase3CriteriaPreview {
  value_potential: string;
  risk_profile: string;
  execution_fit: string;
  strategic_alignment: string;
}

interface StrategicOptionsOverviewProps {
  sessionId: string;
  onContinue?: () => void;
}

export const StrategicOptionsOverview = ({ sessionId, onContinue }: StrategicOptionsOverviewProps) => {
  const [options, setOptions] = useState<StrategicOption[]>([]);
  const [aiReasoning, setAiReasoning] = useState<string>("");
  const [phase3CriteriaPreview, setPhase3CriteriaPreview] = useState<Phase3CriteriaPreview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadOptions();
  }, [sessionId]);

  const loadOptions = async () => {
    try {
      setIsLoading(true);

      // Load scenarios with archetypal data
      const { data: scenarios, error: scenariosError } = await supabase
        .from("scenarios")
        .select("*")
        .eq("session_id", sessionId)
        .eq("scenario_type", "option")
        .order("order_index");

      if (scenariosError) throw scenariosError;

      // If no scenarios or scenarios are empty (no archetype), auto-generate
      if (!scenarios || scenarios.length === 0 || !scenarios[0]?.archetype) {
        console.log("No valid scenarios found, auto-generating...");
        await regenerateOptions();
        return;
      }

      // Load AI reasoning and phase3 criteria from session meta
      const { data: session, error: sessionError } = await supabase
        .from("decision_sessions")
        .select("meta")
        .eq("id", sessionId)
        .single();

      if (sessionError) throw sessionError;

      const sessionMeta = session?.meta as Record<string, any> || {};
      setAiReasoning(sessionMeta.phase2_ai_reasoning || "");
      setPhase3CriteriaPreview(sessionMeta.phase3_criteria_preview || null);

      // Helper to assign archetype based on array index for legacy data
      const getDefaultArchetype = (index: number): "low_risk" | "value_max" | "scaling" => {
        const archetypes: ("low_risk" | "value_max" | "scaling")[] = ["low_risk", "value_max", "scaling"];
        return archetypes[index] || "low_risk";
      };

      // Map scenarios to StrategicOption format - read directly from DB columns
      const mappedOptions: StrategicOption[] = (scenarios || []).map((s: any, index: number) => ({
        id: s.id,
        name: s.name,
        description: s.description || "",
        archetype: s.archetype || getDefaultArchetype(index),
        strategic_goal: s.strategic_goal || "",
        key_advantages: s.key_advantages || [],
        key_risks: s.key_risks || [],
        differentiation_factor: s.differentiation_factor || "",
        user_context_reference: s.user_context_reference || "",
        fits_if: s.fits_if || [],
        does_not_fit_if: s.does_not_fit_if || [],
        order_index: s.order_index
      }));

      setOptions(mappedOptions);
    } catch (error) {
      console.error("Error loading strategic options:", error);
      toast({
        title: "Fehler beim Laden",
        description: "Die strategischen Optionen konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const regenerateOptions = async () => {
    setIsRegenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("phase2-start", {
        body: { session_id: sessionId },
      });

      if (error) throw error;

      toast({
        title: "Optionen neu generiert",
        description: "Drei neue strategische Optionen wurden erstellt.",
      });

      // Reload options
      await loadOptions();
    } catch (error) {
      console.error("Error regenerating options:", error);
      toast({
        title: "Fehler",
        description: "Die Optionen konnten nicht neu generiert werden.",
        variant: "destructive",
      });
    } finally {
      setIsRegenerating(false);
    }
  };

  const getOptionLabel = (index: number): string => {
    const labels = ["Option A", "Option B", "Option C"];
    return labels[index] || `Option ${index + 1}`;
  };

  if (isLoading || isRegenerating) {
    return (
      <div className="space-y-8 max-w-4xl mx-auto">
        {/* Header Skeleton */}
        <div className="space-y-2 animate-fade-in">
          <div className="h-8 w-64 bg-muted/50 rounded-lg animate-pulse" />
          <div className="h-5 w-96 bg-muted/30 rounded-lg animate-pulse" />
        </div>

        {/* Intro Card Skeleton */}
        <div 
          className="rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 to-transparent p-6 animate-fade-in"
          style={{ animationDelay: '100ms' }}
        >
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-full bg-primary/10 animate-pulse" />
            <div className="space-y-3 flex-1">
              <div className="h-4 w-3/4 bg-muted/40 rounded animate-pulse" />
              <div className="h-4 w-1/2 bg-muted/30 rounded animate-pulse" />
            </div>
          </div>
        </div>

        {/* Option Cards Skeleton - Staggered Animation */}
        {[0, 1, 2].map((index) => (
          <div 
            key={index}
            className="rounded-xl border border-border bg-card p-6 space-y-4 animate-fade-in"
            style={{ animationDelay: `${200 + index * 150}ms` }}
          >
            {/* Header */}
            <div className="flex items-center gap-3">
              <div className="h-6 w-20 bg-muted/50 rounded animate-pulse" />
              <div className="h-6 w-32 bg-muted/30 rounded-full animate-pulse" />
            </div>
            
            {/* Title */}
            <div className="h-6 w-2/3 bg-muted/40 rounded animate-pulse" />
            
            {/* Description */}
            <div className="space-y-2">
              <div className="h-4 w-full bg-muted/30 rounded animate-pulse" />
              <div className="h-4 w-4/5 bg-muted/20 rounded animate-pulse" />
            </div>

            {/* Two columns */}
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="space-y-2">
                <div className="h-3 w-24 bg-emerald-500/20 rounded animate-pulse" />
                <div className="h-4 w-full bg-muted/20 rounded animate-pulse" />
                <div className="h-4 w-3/4 bg-muted/20 rounded animate-pulse" />
              </div>
              <div className="space-y-2">
                <div className="h-3 w-24 bg-destructive/20 rounded animate-pulse" />
                <div className="h-4 w-full bg-muted/20 rounded animate-pulse" />
                <div className="h-4 w-3/4 bg-muted/20 rounded animate-pulse" />
              </div>
            </div>
          </div>
        ))}

        {/* Loading indicator */}
        <div className="flex items-center justify-center gap-3 py-4 animate-fade-in" style={{ animationDelay: '650ms' }}>
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">
            {isRegenerating ? "Generiere neue Optionen..." : "Analysiere strategische Möglichkeiten..."}
          </p>
        </div>
      </div>
    );
  }

  if (options.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-muted-foreground">Keine Optionen verfügbar.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Header with more options */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
            Dein Entscheidungsraum
          </h2>
          <p className="text-muted-foreground">
            Jede Option hat einen echten Preis. Klicke zum Aufklappen.
          </p>
        </div>
        
        {/* More Options Dropdown - dezenter */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="shrink-0 text-muted-foreground">
              <MoreHorizontal className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-popover">
            <DropdownMenuItem 
              onClick={() => setShowRegenerateDialog(true)}
              className="gap-2 text-muted-foreground"
            >
              <RefreshCw className="h-4 w-4" />
              Optionen neu generieren
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>


      {/* Intro Card */}
      <StrategicOptionsIntro />

      {/* Options - Compact Accordion Cards */}
      <div className="space-y-4">
        {options.map((option, index) => (
          <StrategicOptionCardCompact
            key={option.id}
            option={option}
            optionLabel={getOptionLabel(index)}
            className="animate-fade-in"
            style={{ animationDelay: `${index * 100}ms` }}
          />
        ))}
      </div>

      {/* AI Reasoning */}
      {aiReasoning && <AIReasoning reasoning={aiReasoning} />}

      {/* Transition Section - Stärkerer CTA */}
      <div className="space-y-5 pt-6 border-t border-border">
        <div className="text-center space-y-1">
          <p className="text-foreground font-medium">
            Bereit für den nächsten Schritt?
          </p>
          <p className="text-muted-foreground text-sm">
            Bewerte jetzt die Optionen anhand deiner Kriterien.
          </p>
        </div>

        <div className="flex justify-center">
          {onContinue && (
            <Button 
              onClick={onContinue} 
              size="lg" 
              className="gap-2 min-w-[260px] text-base font-semibold shadow-lg hover:shadow-xl transition-shadow"
            >
              Weiter zu Phase 3 – Bewertung starten
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Regenerate Dialog */}
      <AlertDialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Optionen neu generieren?</AlertDialogTitle>
            <AlertDialogDescription>
              Achtung: Neue Optionen ersetzen die aktuellen Vorschläge. 
              Deine bisherigen Optionen gehen verloren.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              setShowRegenerateDialog(false);
              regenerateOptions();
            }}>
              Neu generieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
